import { videos } from "@/mocks/videos";
import type { ContentCategory, SortKey, VideoFilters, VideoItem } from "@/types/video";

export const emptyFilters: VideoFilters = {
  genres: [],
  regions: [],
  tags: [],
  status: [],
  platforms: [],
  yearRange: "all",
};

export const yearOptions: string[] = ["all", "2025", "2024", "2023", "2020-2022", "2010-2019"];

export function yearOptionLabel(option: string): string {
  if (option === "all") return "全部年份";
  if (option.includes("-")) {
    const [lo, hi] = option.split("-");
    return `${lo}-${hi}`;
  }
  return option;
}

function matchYear(year: number, range: string): boolean {
  if (range === "all") return true;
  if (range.includes("-")) {
    const [lo, hi] = range.split("-").map((n) => Number(n));
    return year >= lo && year <= hi;
  }
  return year === Number(range);
}

export function getVideoById(id: string): VideoItem | undefined {
  return videos.find((v) => v.id === id);
}

export function getByCategory(category: ContentCategory): VideoItem[] {
  return videos.filter((v) => v.category === category);
}

export function filterVideos(items: VideoItem[], filters: VideoFilters): VideoItem[] {
  return items.filter((item) => {
    if (filters.genres.length > 0 && !filters.genres.some((g) => item.genres.includes(g))) return false;
    if (filters.regions.length > 0 && !filters.regions.includes(item.region)) return false;
    if (filters.tags.length > 0 && !filters.tags.some((t) => item.tags.includes(t))) return false;
    if (filters.status.length > 0 && !(item.updateStatus && filters.status.includes(item.updateStatus))) return false;
    if (filters.platforms.length > 0 && !(item.platform && filters.platforms.includes(item.platform))) return false;
    if (!matchYear(item.year, filters.yearRange)) return false;
    return true;
  });
}

export function sortVideos(items: VideoItem[], sortKey: SortKey): VideoItem[] {
  const list = [...items];
  if (sortKey === "popular") list.sort((a, b) => b.popularity - a.popularity);
  if (sortKey === "rating") list.sort((a, b) => b.rating - a.rating);
  if (sortKey === "recent") {
    list.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }
  return list;
}

export function searchVideos(items: VideoItem[], keyword: string): VideoItem[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return items.filter((item) => {
    const haystack = [item.title, ...item.cast, item.director, ...item.genres, ...item.tags]
      .join(" ")
      .toLowerCase();
    return haystack.includes(kw);
  });
}

export function getRelated(current: VideoItem, limit = 8): VideoItem[] {
  const scored = videos
    .filter((v) => v.id !== current.id)
    .map((v) => {
      let score = 0;
      if (v.category === current.category) score += 2;
      score += v.genres.filter((g) => current.genres.includes(g)).length * 3;
      score += v.tags.filter((t) => current.tags.includes(t)).length;
      return { v, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || b.v.popularity - a.v.popularity);
  return scored.slice(0, limit).map((entry) => entry.v);
}

function unique(list: string[]): string[] {
  return Array.from(new Set(list));
}

export function getFilterOptions(category: ContentCategory) {
  const items = getByCategory(category);
  return {
    genres: unique(items.flatMap((i) => i.genres)),
    regions: unique(items.map((i) => i.region)),
    tags: unique(items.flatMap((i) => i.tags)),
    platforms: unique(items.flatMap((i) => (i.platform ? [i.platform] : []))),
    statuses: unique(items.flatMap((i) => (i.updateStatus ? [i.updateStatus] : []))),
  };
}

export function toggleValue(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

export function hasActiveFilters(filters: VideoFilters): boolean {
  return (
    filters.genres.length > 0 ||
    filters.regions.length > 0 ||
    filters.tags.length > 0 ||
    filters.status.length > 0 ||
    filters.platforms.length > 0 ||
    filters.yearRange !== "all"
  );
}

export function countActiveFilters(filters: VideoFilters): number {
  return (
    filters.genres.length +
    filters.regions.length +
    filters.tags.length +
    filters.status.length +
    filters.platforms.length +
    (filters.yearRange !== "all" ? 1 : 0)
  );
}